import { useState } from 'react';
import { StatusPill } from '@/components/ui/admin/dashboard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import type { RegisteredFieldProps } from '@/lib/component-registry';
import { registerFieldComponent } from '@/lib/component-registry';
import { date, localeFormat } from '@/lib/date';
import { TERM_KIND_LABELS } from '@/lib/terms/variables';
import {
  type TermInvite,
  termAcceptancePdfUrl,
  useSoldierTermAcceptances,
  useTermInvite,
} from '@/services/term-acceptances';

const ACCEPTED_BY_LABELS = {
  participant: 'Aceito pelo participante',
  guardian: 'Aceito pelo responsável',
} as const;

/**
 * Bloco "Termos aceitos" da ficha do recruta (screen "soldiers", `type: 'component',
 * component: 'soldier-terms'`). Só leitura: os aceites são prova e vêm da inscrição do site
 * (skill `terms-acceptance`). Não é campo do formulário — não passa pelo react-hook-form.
 */
function SoldierTerms({ label, recordId }: RegisteredFieldProps) {
  const { data, isLoading } = useSoldierTermAcceptances(recordId);
  const acceptances = data ?? [];

  if (!recordId) return null;

  return (
    <section className="overflow-hidden rounded-[16px] border border-border bg-card">
      <header className="flex items-center gap-3 border-b border-border px-[22px] py-4">
        <Icon name="scroll-text" className="size-4 text-olive-icon" />
        <span className="font-display text-[15px] font-extrabold uppercase tracking-tight text-white">
          {label}
        </span>
      </header>

      {isLoading ? (
        <p className="m-0 px-[22px] py-6 text-[13.5px] text-muted-foreground">
          Carregando…
        </p>
      ) : acceptances.length === 0 ? (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-[22px] py-5">
          <p className="m-0 mr-auto max-w-[60ch] text-[13.5px] text-muted-foreground">
            Nenhum aceite registrado. Inscrições anteriores ao registro dos termos não têm
            aceite guardado — envie o link para o participante (ou o responsável, se for
            menor) ler e aceitar.
          </p>
          <SendTermsButton soldierId={recordId} />
        </div>
      ) : (
        <ul className="m-0 list-none divide-y divide-border p-0">
          {acceptances.map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center gap-x-6 gap-y-3 px-[22px] py-4"
            >
              <span className="mr-auto flex min-w-0 flex-col gap-1.5">
                <span className="flex flex-wrap items-center gap-2.5">
                  <span className="text-[15px] font-semibold text-white">
                    {TERM_KIND_LABELS[a.kind]} · versão {a.version}
                  </span>
                  <StatusPill tone={a.acceptedBy === 'guardian' ? 'sand' : 'olive'}>
                    {ACCEPTED_BY_LABELS[a.acceptedBy]}
                  </StatusPill>
                </span>
                <span className="flex flex-wrap gap-x-[18px] gap-y-1 text-[13px] text-muted-foreground">
                  <span>Em {date(a.acceptedAt).format(localeFormat)}</span>
                  <span>
                    IP {a.ip || '—'}
                    {a.port ? `:${a.port}` : ''}
                  </span>
                  <span className="max-w-[420px] truncate" title={a.userAgent ?? ''}>
                    {a.userAgent || 'Navegador não informado'}
                  </span>
                </span>
                <span
                  className="font-mono text-[11.5px] text-muted-foreground/80"
                  title="SHA-256 do PDF entregue na inscrição"
                >
                  SHA-256 {a.pdfHash}
                </span>
              </span>

              <Button
                type="button"
                variant="outline"
                size="md"
                icon="file-down"
                onClick={() =>
                  window.open(termAcceptancePdfUrl(a.id), '_blank', 'noopener')
                }
              >
                Baixar PDF
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * "Enviar termos para aceite": gera o link do site (`/termos/aceitar/<token>`, 7 dias) pra
 * secretaria mandar pra pessoa. O aceite feito por lá entra com a mesma prova da inscrição.
 */
function SendTermsButton({ soldierId }: { soldierId: string }) {
  const [invite, setInvite] = useState<TermInvite | null>(null);
  const [copied, setCopied] = useState(false);
  const { mutate: createInvite, isPending } = useTermInvite();
  const toast = useToast();

  const handleCopy = async () => {
    if (!invite) return;
    await navigator.clipboard.writeText(invite.url);
    setCopied(true);
    toast.success('Link copiado!');
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="md"
        icon="send"
        disabled={isPending}
        onClick={() => createInvite(soldierId, { onSuccess: setInvite })}
      >
        Enviar termos para aceite
      </Button>

      <Dialog open={!!invite} onOpenChange={(open) => !open && setInvite(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link para aceite dos termos</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Copie e envie para o participante ou, se for menor, para o responsável. O link
            vale até{' '}
            <strong className="text-foreground">
              {invite ? date(invite.expiresAt).format(localeFormat) : ''}
            </strong>{' '}
            e deixa de funcionar depois do aceite.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={invite?.url ?? ''} className="font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={copied ? 'text-green-500' : undefined}
              onClick={handleCopy}
            >
              <Icon name={copied ? 'check-check' : 'copy'} />
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setInvite(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

registerFieldComponent('soldier-terms', SoldierTerms);
